import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Pencil, Search, Trash2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import {
  CO_SCHOLASTIC_TERMS,
  CoScholasticTerm,
  FEEDBACK_TIER_OPTIONS,
  FeedbackTier,
  OrgCoScholasticApi,
  OrgCoScholasticAssessmentRow,
} from '@/services/co-scholastic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<OrgCoScholasticAssessmentRow>();
const TIER_LABEL = Object.fromEntries(FEEDBACK_TIER_OPTIONS.map((o) => [o.value, o.label]));

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.response?.data?.message ?? anyE?.message ?? 'Something went wrong';
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface FormState {
  id: string | null;
  schoolId: string;
  classId: string;
  sectionId: string;
  studentId: string;
  studentLabel: string;
  academicYearId: string;
  categoryId: string;
  term: CoScholasticTerm;
  feedbackTier: FeedbackTier | '';
  assessmentDate: string;
  remarks: string;
}

const BLANK_FORM: FormState = {
  id: null,
  schoolId: '',
  classId: '',
  sectionId: '',
  studentId: '',
  studentLabel: '',
  academicYearId: '',
  categoryId: '',
  term: 'Term 1',
  feedbackTier: '',
  assessmentDate: todayIso(),
  remarks: '',
};

/**
 * Org-tier Co-scholastic Assessment — rebuilt to match real legacy's
 * `co-scholastic-assessment.index` layout exactly: a flat single-record
 * "Create Co-Scholastic Assessment" form (School, Session Year, Class
 * Section, Student, Category, Term, Grade, Assessment Date, Remarks,
 * Submit) plus a "List Co-Scholastic Assessment" table below with
 * edit/delete — not the earlier per-category batch-grading panel (that
 * UX still exists on the backend via batchCreateAssessment, just unused
 * by this page now). Submitting always goes through the SAME upsert
 * endpoint real legacy's separate create/update actions both ultimately
 * write to — editing a row just re-submits with the row's own
 * student/category/term/year, which updates in place.
 */
export function OrgCoScholasticPage() {
  const qc = useQueryClient();
  const dt = useDataTable({ defaultLimit: 20 });

  const [form, setForm] = useState<FormState>(BLANK_FORM);
  const [formError, setFormError] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState<OrgCoScholasticAssessmentRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const [filterSchoolId, setFilterSchoolId] = useState('');

  const isEditing = !!form.id;

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-co-scholastic'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['org-co-scholastic-categories'],
    queryFn: () => OrgCoScholasticApi.categories(),
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['org-co-scholastic-classes', form.schoolId],
    queryFn: () => OrgPromotionApi.classesForSchool(form.schoolId),
    enabled: !!form.schoolId && !isEditing,
  });
  const sections = classes.find((c) => c.id === form.classId)?.sections ?? [];

  const { data: years = [] } = useQuery({
    queryKey: ['org-co-scholastic-years', form.schoolId],
    queryFn: () => OrgPromotionApi.academicYearsForSchool(form.schoolId),
    enabled: !!form.schoolId,
  });

  const { data: students = [] } = useQuery({
    queryKey: ['org-co-scholastic-roster', form.schoolId, form.sectionId],
    queryFn: () => OrgPromotionApi.studentsForSection(form.schoolId, form.sectionId),
    enabled: !!form.schoolId && !!form.sectionId && !isEditing,
  });

  // Picking a school resets everything downstream — ids from one school's schema mean nothing in another's. Not run while editing (school is locked then).
  useEffect(() => {
    if (isEditing) return;
    setForm((f) => ({ ...f, classId: '', sectionId: '', studentId: '', studentLabel: '', academicYearId: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.schoolId]);

  useEffect(() => {
    if (years.length && !form.academicYearId) {
      setForm((f) => ({ ...f, academicYearId: years.find((y) => y.isCurrent)?.id ?? years[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years]);

  const filterParams = { schoolId: filterSchoolId || undefined };

  const { data, isFetching } = useQuery({
    queryKey: ['org-co-scholastic-assessments', dt.params, filterParams],
    queryFn: () => OrgCoScholasticApi.assessments({ ...dt.params, ...filterParams }),
    placeholderData: keepPreviousData,
  });

  const save = useMutation({
    mutationFn: () =>
      OrgCoScholasticApi.createAssessment({
        schoolId: form.schoolId,
        studentId: form.studentId,
        categoryId: form.categoryId,
        academicYearId: form.academicYearId,
        term: form.term,
        feedbackTier: form.feedbackTier as FeedbackTier,
        assessmentDate: form.assessmentDate,
        remarks: form.remarks.trim() || undefined,
      }),
    onSuccess: () => {
      setForm(BLANK_FORM);
      setFormError(undefined);
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['org-co-scholastic-assessments'] });
    },
    onError: (e) => setFormError(errMsg(e)),
  });

  const remove = useMutation({
    mutationFn: (r: OrgCoScholasticAssessmentRow) => OrgCoScholasticApi.removeAssessment(r.id, r.schoolId),
    onSuccess: () => {
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['org-co-scholastic-assessments'] });
    },
    onError: (e) => setDeleteError(errMsg(e)),
  });

  function startEdit(r: OrgCoScholasticAssessmentRow) {
    setFormError(undefined);
    setSaved(false);
    setForm({
      id: r.id,
      schoolId: r.schoolId,
      classId: '',
      sectionId: '',
      studentId: r.studentId,
      studentLabel: `${r.studentName ?? '(no linked user)'}${r.admissionNumber ? ` (${r.admissionNumber})` : ''}`,
      academicYearId: r.academicYearId,
      categoryId: r.categoryId,
      term: r.term,
      feedbackTier: r.feedbackTier,
      assessmentDate: r.assessmentDate.slice(0, 10),
      remarks: r.remarks ?? '',
    });
  }

  const COLUMNS = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('schoolName', { header: 'School' }),
    columnHelper.display({
      id: 'studentName',
      header: 'Student',
      cell: (ctx) => ctx.row.original.studentName ?? '(no linked user)',
    }),
    columnHelper.accessor('categoryName', { header: 'Category' }),
    columnHelper.display({
      id: 'feedbackTier',
      header: 'Grade',
      cell: (ctx) => <Badge tone="blue">{TIER_LABEL[ctx.row.original.feedbackTier] ?? ctx.row.original.feedbackTier}</Badge>,
    }),
    columnHelper.accessor('term', { header: 'Term' }),
    columnHelper.display({
      id: 'assessmentDate',
      header: 'Date',
      cell: (ctx) => ctx.row.original.assessmentDate.slice(0, 10),
    }),
    columnHelper.display({
      id: 'remarks',
      header: 'Remarks',
      enableSorting: false,
      cell: (ctx) => ctx.row.original.remarks || '—',
    }),
    columnHelper.display({
      id: 'action',
      header: 'Action',
      enableSorting: false,
      cell: (ctx) => (
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            title="Edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
            onClick={() => startEdit(ctx.row.original)}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Delete"
            className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => {
              setDeleteError(undefined);
              setDeleting(ctx.row.original);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </span>
      ),
    }),
  ];

  const canSubmit =
    !!form.schoolId &&
    !!form.studentId &&
    !!form.categoryId &&
    !!form.academicYearId &&
    !!form.feedbackTier &&
    !!form.assessmentDate;

  return (
    <>
      <PageHeader title="Co-Scholastic Assessment" description="Create and manage co-scholastic assessments across your organization's schools." />

      <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-800">
          {isEditing ? 'Edit Co-Scholastic Assessment' : 'Create Co-Scholastic Assessment'}
        </h3>
        {formError && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{formError}</div>
        )}
        {saved && !isEditing && (
          <div className="mb-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            Co-Scholastic Assessment created successfully.
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="School" required>
            <Select
              value={form.schoolId}
              onChange={(e) => setForm({ ...form, schoolId: e.target.value })}
              disabled={isEditing}
            >
              <option value="">Select School</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Session Year" required>
            <Select
              value={form.academicYearId}
              onChange={(e) => setForm({ ...form, academicYearId: e.target.value })}
              disabled={!form.schoolId}
            >
              <option value="">Select Session Year</option>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                  {y.isCurrent ? ' (current)' : ''}
                </option>
              ))}
            </Select>
          </Field>

          {!isEditing ? (
            <>
              <Field label="Class" required>
                <Select
                  value={form.classId}
                  onChange={(e) => setForm({ ...form, classId: e.target.value, sectionId: '', studentId: '' })}
                  disabled={!form.schoolId}
                >
                  <option value="">Select Class</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Section" required>
                <Select
                  value={form.sectionId}
                  onChange={(e) => setForm({ ...form, sectionId: e.target.value, studentId: '' })}
                  disabled={!form.classId}
                >
                  <option value="">Select Section</option>
                  {sections.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Student" required>
                <Select
                  value={form.studentId}
                  onChange={(e) => setForm({ ...form, studentId: e.target.value })}
                  disabled={!form.sectionId}
                >
                  <option value="">Select Student</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name ?? '(no linked user)'} {s.rollNumber ? `— Roll ${s.rollNumber}` : ''} ({s.admissionNo})
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          ) : (
            <Field label="Student" required>
              <Select value={form.studentId} disabled>
                <option value={form.studentId}>{form.studentLabel}</option>
              </Select>
            </Field>
          )}

          <Field label="Category" required>
            <Select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
              <option value="">Select Category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Term" required>
            <Select value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value as CoScholasticTerm })}>
              {CO_SCHOLASTIC_TERMS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Grade" required>
            <Select
              value={form.feedbackTier}
              onChange={(e) => setForm({ ...form, feedbackTier: e.target.value as FeedbackTier })}
            >
              <option value="">Select Grade</option>
              {FEEDBACK_TIER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Assessment Date" required>
            <Input type="date" value={form.assessmentDate} onChange={(e) => setForm({ ...form, assessmentDate: e.target.value })} />
          </Field>
        </div>

        <Field label="Remarks" className="mt-4">
          <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} />
        </Field>

        <div className="mt-4 flex items-center gap-2">
          <button className="btn-primary" disabled={!canSubmit || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : isEditing ? 'Update' : 'Submit'}
          </button>
          {isEditing && (
            <button className="btn-secondary" onClick={() => setForm(BLANK_FORM)}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3 className="mb-2 text-sm font-semibold text-slate-800">List Co-Scholastic Assessment</h3>
      {data?.truncated && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          This organization has more assessments than this report can show at once — results are truncated.
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by student name"
            className="w-72 pl-9"
          />
        </div>
        <Select className="!w-56" value={filterSchoolId} onChange={(e) => setFilterSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={COLUMNS}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No co-scholastic assessments found."
        getRowId={(row) => `${row.schoolId}:${row.id}`}
      />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate(deleting!)}
        title="Delete assessment?"
        message={deleteError ?? `This will permanently delete this assessment for "${deleting?.studentName}". This cannot be undone.`}
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}
